import React from 'react';
import { Invoice, InvoiceStatus } from '../../types';
import { effectiveInvoiceStatus, daysOverdue } from '../../services/invoiceLogic';

/**
 * Statusanzeige einer Rechnung.
 *
 * Zeigt bewusst den ABGELEITETEN Status: eine offene Rechnung mit
 * verstrichener Faelligkeit erscheint als ueberfaellig, obwohl in der Datenbank
 * weiterhin SENT steht. Ueberfaelligkeit ist eine Folge des Datums und wird
 * nicht gespeichert — sonst muesste ein naechtlicher Job den Status
 * festgeschriebener Rechnungen umschreiben.
 */

const META: Record<InvoiceStatus, { label: string; className: string }> = {
    DRAFT: { label: 'Entwurf', className: 'bg-slate-100 text-slate-700 border-slate-200' },
    READY_FOR_REVIEW: { label: 'In Prüfung', className: 'bg-blue-50 text-blue-700 border-blue-200' },
    APPROVED: { label: 'Freigegeben', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    // Zwischenzustand waehrend der Ausstellung. Sichtbar nur, wenn die
    // Ausstellung haengengeblieben ist — dann soll man es auch sehen.
    ISSUING: { label: 'Wird ausgestellt …', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    ISSUED: { label: 'Ausgestellt', className: 'bg-sky-50 text-sky-700 border-sky-200' },
    SENT: { label: 'Versendet', className: 'bg-sky-50 text-sky-700 border-sky-200' },
    PARTIALLY_PAID: {
        label: 'Teilweise bezahlt',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
    },
    PAID: { label: 'Bezahlt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    PARTIALLY_CREDITED: {
        label: 'Teilw. gutgeschrieben',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
    },
    CREDITED: {
        label: 'Gutgeschrieben',
        className: 'bg-violet-50 text-violet-700 border-violet-200',
    },
    REFUND_PENDING: {
        label: 'Erstattung offen',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
    },
    REFUNDED: { label: 'Erstattet', className: 'bg-violet-50 text-violet-700 border-violet-200' },
    DISPUTED: { label: 'Strittig', className: 'bg-orange-50 text-orange-800 border-orange-200' },
    OVERDUE: { label: 'Überfällig', className: 'bg-red-50 text-red-700 border-red-200' },
    SUPERSEDED_BY_FINAL_INVOICE: {
        label: 'Durch Schlussrechnung abgelöst',
        className: 'bg-slate-100 text-slate-600 border-slate-200',
    },
    CANCELLED: {
        label: 'Storniert',
        className: 'bg-slate-100 text-slate-500 border-slate-200 line-through',
    },
};

const statusLabel = (status: InvoiceStatus): string => META[status]?.label ?? status;

interface Props {
    invoice: Pick<Invoice, 'status' | 'dueDate'>;
    /** "Heute" wird hereingereicht, damit die Anzeige testbar und konsistent bleibt. */
    today: string;
    showDays?: boolean;
}

export const InvoiceStatusBadge: React.FC<Props> = ({ invoice, today, showDays = true }) => {
    const status = effectiveInvoiceStatus(invoice, today);
    const meta = META[status] ?? META.DRAFT;
    const overdue = status === 'OVERDUE' ? daysOverdue(invoice, today) : 0;

    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.className}`}
        >
            {statusLabel(status)}
            {showDays && overdue > 0 && (
                <span className="font-semibold">
                    · {overdue} {overdue === 1 ? 'Tag' : 'Tage'}
                </span>
            )}
        </span>
    );
};

export type ProjectCheckTrafficLevel = 'green' | 'yellow' | 'red';

const TRAFFIC: Record<
    ProjectCheckTrafficLevel,
    { label: string; className: string; dot: string }
> = {
    green: {
        label: 'Projekt geprüft',
        className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        dot: 'bg-emerald-500',
    },
    yellow: {
        label: 'Prüfung nötig',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
        dot: 'bg-amber-500',
    },
    red: {
        label: 'Gesperrt',
        className: 'bg-red-50 text-red-700 border-red-200',
        dot: 'bg-red-500',
    },
};

/**
 * Ampel des Projektchecks. Die Regeln selbst kommen erst mit M3; der Chip ist
 * schon hier, damit Liste, Editor und spaetere Pruefqueue dieselbe Darstellung
 * benutzen und nicht jede ihre eigene erfindet.
 */
export const ProjectCheckChip: React.FC<{ traffic: ProjectCheckTrafficLevel; count?: number }> = ({
    traffic,
    count,
}) => {
    const t = TRAFFIC[traffic];
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${t.className}`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
            {t.label}
            {count != null && count > 0 && <span className="font-semibold">({count})</span>}
        </span>
    );
};
